import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import EmptyData from "@/components/EmptyData";
import VLFSelectNew from "@/components/Input/SelectNew";
import Button from "@mui/material/Button";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";
import {ScoutingReportAPI} from "@/api/scouting_report";

import {ERROR_OPERATION} from "@/utils/constants";
import {generateInternalComparisonPDF} from "@/utils/generateInternalComparisonPDF";
import "./index.scss";

const getBirthYear = (player) => {
	const birthDate =
		player?.dateOfBirth ||
		player?.birthDate ||
		player?.player?.dateOfBirth ||
		player?.player?.birthDate ||
		null;
	return birthDate ? moment(birthDate).year() : null;
};

const getRoleKey = (player) => {
	// if (player?.goalkeeper) {
	// 	return "P";
	// }
	return player?.primaryRole || player?.player?.primaryRole || "ND";
};

const getRoleOrder = (roleKey) => {
	if (!roleKey) {
		return 99;
	}
	const normalized = roleKey.toString().toLowerCase();
	if (normalized.startsWith("port")) return 1;
	if (normalized.startsWith("dif")) return 2;
	if (normalized.startsWith("centro")) return 3;
	if (normalized.startsWith("att")) return 4;
	return 99;
};

const getPlayerLabel = (player) =>
	`${player?.firstName || ""} ${player?.lastName || ""}`.trim();

const parseRating = (value) => {
	if (value === null || value === undefined) return null;
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? null : parsed;
};

const getValuationRating = (valuation) => {
	return (
		parseRating(valuation?.finalAverage) ??
		parseRating(valuation?.finalOverallRating) ??
		parseRating(valuation?.finalOverallValuation) ??
		parseRating(valuation?.valuation) ??
		parseRating(valuation?.rating) ??
		null
	);
};

function ScoutingInternalComparisonPage(props) {
	const {handleNotification} = props;
	const seasonId = useSelector((state) => state?.season?.id);
	const season = useSelector((state) => state?.season);

	const [loading, setLoading] = useState(false);
	const [comparisonData, setComparisonData] = useState([]);
	const [birthYearFilter, setBirthYearFilter] = useState();

	const seasonStartDate = useMemo(
		() => season?.startDate || moment().startOf("year").format("YYYY-MM-DD"),
		[season],
	);
	const seasonEndDate = useMemo(
		() => season?.endDate || moment().endOf("year").format("YYYY-MM-DD"),
		[season],
	);

	const buildValuationStats = (valuations, allowedIds) => {
		const map = {};
		valuations?.forEach((v) => {
			const playerId = v.player?.id;

			if (!playerId || (allowedIds && !allowedIds.has(playerId))) {
				return;
			}
			const rating = getValuationRating(v);
			if (rating === null) {
				return;
			}
			if (!map[playerId]) {
				map[playerId] = {sum: 0, count: 0};
			}
			map[playerId].sum += rating;
			map[playerId].count += 1;
		});

		return map;
	};

	const loadComparison = async () => {
		setLoading(true);
		try {
			const [
				internalPlayersResponse,
				externalPlayersResponse,
				internalValuations,
				externalValuations,
				scoutingReports,
			] = await Promise.all([
				PlayerAPI.list({prospect: false, seasonId}),
				PlayerAPI.list({prospect: true, seasonId}),
				PlayerValuationAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					isInternal: true,
					seasonId,
				}),
				PlayerValuationAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					isInternal: false,
					seasonId,
				}),
				ScoutingReportAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					seasonId,
				}),
			]);

			const internalPlayers = (internalPlayersResponse || []).filter(
				(p) => p.status === "Interno",
			);
			const externalPlayers = externalPlayersResponse || [];

			const internalIds = new Set(internalPlayers.map((p) => p.id));
			const externalIds = new Set(externalPlayers.map((p) => p.id));

			const internalStats = buildValuationStats(
				internalValuations,
				internalIds,
			);
			const externalStats = buildValuationStats(
				[...(externalValuations || []), ...(scoutingReports || [])],
				externalIds,
			);

			const externalGroupStats = {};
			externalPlayers.forEach((player) => {
				const birthYear = getBirthYear(player);
				if (!birthYear) {
					return;
				}
				const groupKey = `${birthYear}|${getRoleKey(player)}`;
				const stats = externalStats[player.id];
				if (!stats || !stats.count) {
					return;
				}
				if (!externalGroupStats[groupKey]) {
					externalGroupStats[groupKey] = {
						sum: 0,
						count: 0,
						players: 0,
						bestAvg: null,
						bestPlayer: null,
						playerAvgs: [],
					};
				}
				const group = externalGroupStats[groupKey];
				group.sum += stats.sum;
				group.count += stats.count;
				group.players += 1;

				const avg = stats.sum / stats.count;
				group.playerAvgs.push({player, avg});
				if (group.bestAvg === null || avg > group.bestAvg) {
					group.bestAvg = avg;
					group.bestPlayer = player;
				}
			});

			const internalByYear = internalPlayers.reduce((acc, player) => {
				const year = getBirthYear(player);
				if (!year) {
					return acc;
				}
				if (!acc[year]) {
					acc[year] = [];
				}
				acc[year].push(player);
				return acc;
			}, {});

			const result = Object.keys(internalByYear)
				.sort((a, b) => Number(b) - Number(a))
				.map((birthYear) => {
					const rows = internalByYear[birthYear]
						.map((player) => {
							const roleKey = getRoleKey(player);
							const internalStat = internalStats[player.id];
							const internalAvg =
								internalStat && internalStat.count
									? internalStat.sum / internalStat.count
									: null;
							const groupKey = `${birthYear}|${roleKey}`;
							const externalGroup = externalGroupStats[groupKey];
							const externalAvg =
								externalGroup && externalGroup.count
									? externalGroup.sum / externalGroup.count
									: null;
							const externalBestPlayer = externalGroup?.bestPlayer || null;
							const externalBestName = externalBestPlayer
								? `${externalBestPlayer.firstName || ""} ${
										externalBestPlayer.lastName || ""
									}`.trim()
								: null;
							const externalTopList = (externalGroup?.playerAvgs || [])
								.slice()
								.sort((a, b) => b.avg - a.avg)
								.map((entry) => ({
									name: `${entry.player?.firstName || ""} ${
										entry.player?.lastName || ""
									}`.trim(),
									avg: entry.avg,
								}));
							const delta =
								internalAvg !== null && externalGroup?.bestAvg
									? externalGroup.bestAvg - internalAvg
									: null;

							return {
								player,
								roleKey,
								internalAvg,
								externalAvg,
								externalPlayers: externalGroup?.players || 0,
								externalBestName,
								externalTopList,
								externalBestAvg: externalGroup?.bestAvg ?? null,
								delta,
							};
						})
						.filter(
							(row) => row.internalAvg !== null || row.externalAvg !== null,
						)
						.sort((a, b) => {
							const roleOrderDiff =
								getRoleOrder(a.roleKey) - getRoleOrder(b.roleKey);
							if (roleOrderDiff !== 0) {
								return roleOrderDiff;
							}
							const ratingA = a.internalAvg ?? -Infinity;
							const ratingB = b.internalAvg ?? -Infinity;
							if (ratingB !== ratingA) {
								return ratingB - ratingA;
							}
							return getPlayerLabel(a.player).localeCompare(
								getPlayerLabel(b.player),
							);
						});

					const bestByRole = rows.reduce((acc, row) => {
						if (!row || row.internalAvg === null) {
							return acc;
						}
						const currentBest = acc[row.roleKey];
						if (!currentBest || row.internalAvg > currentBest.internalAvg) {
							acc[row.roleKey] = row;
						}
						return acc;
					}, {});

					return {
						birthYear,
						rows,
						bestByRole,
					};
				})
				.filter((item) => item.rows.length);

			setComparisonData(result);
		} catch (error) {
			console.error("Errore nel confronto scouting:", error);
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (seasonId) {
			loadComparison();
		}
	}, [seasonId, seasonStartDate, seasonEndDate]);

	const birthYearOptions = useMemo(() => {
		return comparisonData.map((item) => ({
			value: item.birthYear,
			label: item.birthYear,
		}));
	}, [comparisonData]);

	const filteredComparisonData = useMemo(() => {
		if (!birthYearFilter) {
			return comparisonData;
		}
		return comparisonData.filter(
			(item) => item.birthYear === birthYearFilter.value,
		);
	}, [comparisonData, birthYearFilter]);

	return (
		<Page className="scouting-internal-comparison-page">
			<Loading visible={loading} />
			<div className="row no-margin col-container g-2">
				<div className="col-12 padding-small d-flex justify-content-end">
					<Button
						variant="contained"
						startIcon={<PictureAsPdfIcon />}
						onClick={() => generateInternalComparisonPDF(filteredComparisonData)}
						disabled={!filteredComparisonData.length}
					>
						Scarica PDF
					</Button>
				</div>
				<div className="col-12 padding-small">
					<VLFCard
						title="Filtri"
						openable={false}
						content={
							<div className="row no-margin">
								<div className="col-12 col-md-4">
									<VLFSelectNew
										options={birthYearOptions}
										sorted={false}
										value={birthYearFilter}
										onChange={setBirthYearFilter}
										label="Anno di nascita"
										clearable
									/>
								</div>
							</div>
						}
					/>
				</div>
				{filteredComparisonData.length ? (
					filteredComparisonData.map((yearBlock) => (
						<div key={yearBlock.birthYear} className="col-12 padding-small">
							<VLFCard
								title={`${yearBlock.birthYear}`}
								content={
									<div className="comparison-table">
										<div className="comparison-row comparison-header">
											<div className="cell player">Giocatore</div>
											<div className="cell value">Rating</div>
											<div className="cell delta">Diff.</div>
										</div>
										{yearBlock.rows.map((row) => {
											const betterExternal =
												row.internalAvg !== null &&
												row.externalAvg !== null &&
												row.externalAvg > row.internalAvg;
											const roleBest =
												yearBlock.bestByRole?.[row.roleKey] || null;
											const isBestPlayer =
												roleBest && row.player?.id === roleBest.player?.id;
											return (
												<div key={row.player.id} className="comparison-row">
													<div className="cell player">
														<div className="player-main">
															<span className="player-name">
																{row.player?.firstName} {row.player?.lastName}
															</span>
															<span className="player-role">{row.roleKey}</span>
															{isBestPlayer ? (
																<span className="player-best">★ Best</span>
															) : null}
														</div>
														<div className="player-sub">
															{/* <span className="player-sub-label">
																Esterno:
															</span> */}
															{row.externalTopList?.length ? (
																<span className="player-sub-list">
																	{row.externalTopList.map((entry, idx) => (
																		<span
																			key={`${entry.name}-${idx}`}
																			className={
																				"player-sub-item " +
																				(idx === 0 ? "best" : "")
																			}
																		>
																			{entry.name || "---"}{" "}
																			{entry.avg !== null &&
																			entry.avg !== undefined
																				? `(${entry.avg.toFixed(2)})`
																				: ""}
																		</span>
																	))}
																</span>
															) : (
																<span className="player-sub-value">---</span>
															)}
															{/* {row.externalPlayers ? (
																<span className="player-sub-meta">
																	{" "}
																	• {row.externalPlayers}
																</span>
															) : null} */}
														</div>
													</div>
													<div className="cell value">
														{row.internalAvg !== null
															? row.internalAvg.toFixed(2)
															: "---"}
													</div>
													<div className="cell delta">
														{row.delta !== null ? (
															<span
																className={
																	"delta-badge " +
																	(betterExternal ? "warning" : "success")
																}
															>
																{row.delta > 0 ? "+" : ""}
																{row.delta.toFixed(2)}
															</span>
														) : (
															"---"
														)}
													</div>
												</div>
											);
										})}
									</div>
								}
							/>
						</div>
					))
				) : (
					<div className="col-12 padding-small">
						<VLFCard
							title="Nessun dato presente"
							openable={false}
							content={
								<div className="comparison-empty-state">
									<div className="comparison-empty-icon">📊</div>
									<div className="comparison-empty-title">
										Nessun dato disponibile
									</div>
									<div className="comparison-empty-subtitle">
										Seleziona un altro anno o attendi nuove valutazioni.
									</div>
									<div className="comparison-empty-body">
										<EmptyData />
									</div>
								</div>
							}
						/>
					</div>
				)}
			</div>
		</Page>
	);
}

export default ScoutingInternalComparisonPage;
